import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiHome,
  FiCompass,
  FiPlus,
  FiSearch,
  FiUsers,
  FiStar,
  FiActivity,
  FiX,
  FiMenu,
  FiBookmark,
} from "react-icons/fi";

// Sub-components
import FeedSection from "./FeedSection";
import DiscoverySection from "./DiscoverySection";
import SidebarWidgets from "./SidebarWidgets";
import CreateCommunityModal from "../CreateCommunityModal";
import SearchResultsSection from "./SearchResultsSection";
import SavedThreadsView from "../feed/SavedThreadsView";

// Styles
import "./collabhub-variables.css";
import "./collabhub-main.css";

// Animation variants for Framer Motion
const pageVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94],
      staggerChildren: 0.1,
    },
  },
};

const headerVariants = {
  hidden: { opacity: 0, y: -20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
};

const tabVariants = {
  hidden: { opacity: 0, scale: 0.98 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.3,
      ease: "easeOut",
    },
  },
  exit: {
    opacity: 0,
    scale: 0.98,
    transition: {
      duration: 0.2,
    },
  },
};

/**
 * CommunitiesHub - Main Communities Page
 * State of the art UI with Feed and Discovery tabs
 * 
 * NOTE: Thread detail view is now handled by PersistentCommunitiesLayout
 * which renders ThreadDetailSection via React Router Outlet pattern.
 * This keeps CommunitiesHub always mounted for scroll position preservation.
 */
const CommunitiesHub = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get initial tab from URL params
  const tabFromUrl = searchParams.get("tab");
  const validTabs = ["feed", "discovery", "saved"];
  const initialTab = validTabs.includes(tabFromUrl) ? tabFromUrl : "feed";

  // Tab state
  const [activeTab, setActiveTab] = useState(initialTab);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);

  // Sync tab with URL param
  useEffect(() => {
    if (tabFromUrl && validTabs.includes(tabFromUrl) && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);

  // Handle tab change
  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    // Update URL with tab param
    if (tab === "feed") {
      searchParams.delete("tab");
    } else {
      searchParams.set("tab", tab);
    }
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Toggle mobile sidebar
  const toggleMobileSidebar = useCallback(() => {
    setShowMobileSidebar((prev) => !prev);
  }, []);

  // Close mobile sidebar
  const closeMobileSidebar = useCallback(() => {
    setShowMobileSidebar(false);
  }, []);

  // Handle community creation success
  const handleCommunityCreated = useCallback(
    (newCommunity) => {
      toast.success("Community created successfully!");
      setShowCreateModal(false);
      // Navigate to the new community
      if (newCommunity?.data?.community?._id) {
        navigate(`/communities/${newCommunity.data.community._id}`);
      }
    },
    [navigate]
  );

  // Handle search with debouncing
  const handleSearch = useCallback(
    (e) => {
      const value = e.target.value;
      setSearchQuery(value);
    },
    []
  );

  // Clear search
  const handleClearSearch = useCallback(() => {
    setSearchQuery("");
    setIsSearchFocused(false);
  }, []);

  // Determine if search is active (query has 2+ characters)
  const isSearchActive = searchQuery.trim().length >= 2;

  // Tab configuration
  const tabs = [
    {
      id: "feed",
      label: "Feed",
      icon: FiHome,
      description: "Your personalized community feed",
    },
    {
      id: "discovery",
      label: "Discover",
      icon: FiCompass,
      description: "Find new communities",
    },
    {
      id: "saved",
      label: "Saved",
      icon: FiBookmark,
      description: "Your saved threads",
    },
  ];

  return (
    <motion.div
      className="chub-page"
      variants={pageVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Decorative background gradient */}
      <div className="chub-bg-gradient" />

      <div className="chub-container">
        {/* Header Section */}
        <motion.header className="chub-header" variants={headerVariants}>
          <div className="chub-header-content">
            {/* Logo/Title */}
            <div className="chub-header-brand">
              <div className="chub-header-icon">
                <FiUsers size={22} />
              </div>
              <div className="chub-header-text">
                <h1 className="chub-header-title">Communities</h1>
                <p className="chub-header-subtitle">
                  Connect • Collaborate • Grow
                </p>
              </div>
            </div>

            {/* Search Bar */}
            <div
              className={`chub-search-wrapper ${
                isSearchFocused ? "focused" : ""
              }`}
            >
              <FiSearch className="chub-search-icon" size={18} />
              <input
                type="text"
                className="chub-search-input"
                placeholder="Search communities, topics, or members..."
                value={searchQuery}
                onChange={handleSearch}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
              />
              {searchQuery && (
                <motion.button
                  className="chub-search-clear"
                  onClick={() => setSearchQuery("")}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  ×
                </motion.button>
              )}
            </div>

            {/* Actions */}
            <div className="chub-header-actions">
              <motion.button
                className="chub-btn chub-btn-create"
                onClick={() => setShowCreateModal(true)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <FiPlus size={18} />
                <span>Create</span>
              </motion.button>
            </div>
          </div>

          {/* Tab Navigation */}
          <nav className="chub-tabs">
            <div className="chub-tabs-list">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;

                return (
                  <motion.button
                    key={tab.id}
                    className={`chub-tab ${isActive ? "active" : ""}`}
                    onClick={() => handleTabChange(tab.id)}
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Icon size={18} />
                    <span>{tab.label}</span>
                    {isActive && (
                      <motion.div
                        className="chub-tab-indicator"
                        layoutId="activeTab"
                        transition={{
                          type: "spring",
                          stiffness: 400,
                          damping: 30,
                        }}
                      />
                    )}
                  </motion.button>
                );
              })}
            </div>

            {/* Quick Stats */}
            <div className="chub-quick-stats">
              <div className="chub-stat">
                <FiActivity size={14} />
                <span>12 Active</span>
              </div>
              <div className="chub-stat">
                <FiStar size={14} />
                <span>5 New</span>
              </div>
            </div>
          </nav>
        </motion.header>

        {/* Main Content Layout */}
        <div className="chub-layout">
          {/* Main Content Area */}
          <main className="chub-main">
            <AnimatePresence mode="wait">
              {/* Search Results View - Shows when searching */}
              {isSearchActive && (
                <motion.div
                  key="search-results"
                  variants={tabVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <SearchResultsSection 
                    query={searchQuery} 
                    onClearSearch={handleClearSearch}
                  />
                </motion.div>
              )}
              
              {/* Feed View */}
              {!isSearchActive && activeTab === "feed" && (
                <motion.div
                  key="feed"
                  variants={tabVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <FeedSection searchQuery={searchQuery} />
                </motion.div>
              )}
              
              {/* Discovery View */}
              {!isSearchActive && activeTab === "discovery" && (
                <motion.div
                  key="discovery"
                  variants={tabVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <DiscoverySection searchQuery={searchQuery} />
                </motion.div>
              )}

              {/* Saved Threads View */}
              {!isSearchActive && activeTab === "saved" && (
                <motion.div
                  key="saved"
                  variants={tabVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <SavedThreadsView />
                </motion.div>
              )}
            </AnimatePresence>
          </main>

          {/* Desktop Sidebar - hidden on mobile */}
          <aside className="chub-sidebar chub-sidebar-desktop">
            <SidebarWidgets
              onCreateCommunity={() => setShowCreateModal(true)}
            />
          </aside>
        </div>

        {/* Mobile Floating Action Button */}
        <motion.button
          className="chub-fab"
          onClick={toggleMobileSidebar}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          aria-label="Open sidebar"
        >
          <FiMenu size={22} />
        </motion.button>

        {/* Mobile Sidebar Drawer */}
        <AnimatePresence>
          {showMobileSidebar && (
            <>
              {/* Backdrop */}
              <motion.div
                className="chub-drawer-backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={closeMobileSidebar}
              />

              {/* Drawer - Slides up from bottom */}
              <motion.aside
                className="chub-drawer"
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "100%" }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
              >
                <div className="chub-drawer-header">
                  <h3 className="chub-drawer-title">Quick Access</h3>
                  <motion.button
                    className="chub-drawer-close"
                    onClick={closeMobileSidebar}
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <FiX size={20} />
                  </motion.button>
                </div>
                <div className="chub-drawer-content">
                  <SidebarWidgets
                    onCreateCommunity={() => {
                      closeMobileSidebar();
                      setShowCreateModal(true);
                    }}
                  />
                </div>
              </motion.aside>
            </>
          )}
        </AnimatePresence>
      </div>

      {/* Create Community Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <CreateCommunityModal
            onClose={() => setShowCreateModal(false)}
            onSuccess={handleCommunityCreated}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default CommunitiesHub;
